import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AccessGroup } from '@/api/auth';
import type { PersonCard } from '@/api/people';
import { Button } from '@/components/ui/Button';
import { DropdownMenu, type MenuItem } from '@/components/ui/DropdownMenu';
import { mayAnonymizePerson, mayWriteAboutPerson } from '@/pages/Candidates/candidateAccess';

import { AnonymizeDialog } from './AnonymizeDialog';
import { EditPersonDialog } from './EditPersonDialog';

interface Props {
  person: PersonCard;
  groups: AccessGroup[];
}

/**
 * §6.5's header overflow menu: *"Edit, and for Admin, **Anonymize**"*.
 *
 * ⚠️ **There is no Delete and there will not be one** (ADR 0009, feedback protection #6). §7 names
 * *"Add a Delete person action"* as the edit somebody will attempt on this screen; the answer is
 * anonymisation, which keeps the ratings, the verdicts and the whole status history and clears only
 * the personal data. `personCard.test.tsx` asserts no control on this card matches `/delete|remove/i`.
 *
 * ⚠️ **An empty menu is not rendered.** For an Interviewer there is nothing in it (§5: hidden, never
 * disabled — `candidateAccess.ts` cites the four matrix rows), and on an anonymised person *Edit* is
 * gone too: `PATCH /people/{id}/` answers **409** for them, so it is a control that cannot work
 * (ADR 0009 — *"a recruiter retyping a name here would silently undo a lawful erasure"*).
 *
 * ⚠️ **The three status actions are a different question and stay** — they live in each consideration
 * block, and the backend deliberately does **not** close them on an anonymised person: the six write
 * paths `refuse_if_anonymized` shuts are the person, their considerations and the four interview
 * routes, and a run still has to be closable after an erasure. ADR 0009's whole argument is that the
 * panel's record survives.
 *
 * ⚠️ **Anonymize is the one item whose gate is *not* the same question** (TASK-067). It is **Admin
 * only** — `mayAnonymizePerson`, one matrix row, `(401, 403, 403, 200, 403, 403)` — so the Recruiter
 * who may *edit* this person is shown no such item, and it is **hidden rather than disabled** for the
 * reason §5 gives: a disabled control still tells the viewer the capability exists and is being
 * withheld from them.
 *
 * ⚠️ **And it is the one item that *stays* on an already-anonymised person.** The call is idempotent
 * and keeps the original `anonymized_at`, and a second pass is the documented remedy if text ever
 * arrives about an erased person (ADR 0009). So the two gates pull in opposite directions on the same
 * card — *Edit* disappears, *Anonymize* does not — and an anonymised card therefore has a menu for an
 * Admin and no menu at all for a Recruiter.
 */
export function CardMenu({ person, groups }: Props) {
  const { t } = useTranslation('candidates');
  const [editing, setEditing] = useState(false);
  const [anonymizing, setAnonymizing] = useState(false);

  const anonymized = person.anonymized_at !== null;
  const mayEdit = mayWriteAboutPerson(groups) && !anonymized;

  const items: MenuItem[] = [];
  if (mayEdit) items.push({ label: t('card.menu.edit'), onSelect: () => setEditing(true) });
  if (mayAnonymizePerson(groups))
    items.push({
      label: t('card.menu.anonymize'),
      // `negative` text, and it is still a word rather than colour alone (`DropdownMenu`). The word is
      // *Anonymize* and never *Delete*: ADR 0009 is anonymise **instead of** deleting, and labelling
      // it as a deletion would misdescribe both what it does and what survives it (§7's predicted
      // edit is *"Add a Delete person action"*, and this item is the answer to it, not a rename of it).
      destructive: true,
      onSelect: () => setAnonymizing(true),
    });

  if (items.length === 0) return null;

  return (
    <>
      <DropdownMenu
        trigger={
          <Button
            variant="ghost"
            size="sm"
            // §4 Icons: an icon is never the only label — the accessible name is the label here, and
            // the menu's own items carry words. A `⋯` trigger is what §6.5 means by "overflow menu".
            aria-label={t('card.menu.label')}
          >
            <MoreHorizontal size={16} strokeWidth={1.5} aria-hidden />
          </Button>
        }
        items={items}
      />

      {/* Mounted only while open, so the form's defaults are the card as it is now (`EditPersonDialog`). */}
      {editing && <EditPersonDialog person={person} onClose={() => setEditing(false)} />}

      {/* Mounted only while open for the same reason, and one that matters more here: a cancelled
          confirmation must not leave its acknowledgement ticked for the next time (`AnonymizeDialog`). */}
      {anonymizing && <AnonymizeDialog person={person} onClose={() => setAnonymizing(false)} />}
    </>
  );
}
